import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, esES, GridColDef, GridFilterModel } from '@mui/x-data-grid';
import { Button, DialogActions, DialogContent, FormControl, Grid, IconButton, LinearProgress,  MenuItem,  OutlinedInput,  Select,  SelectChangeEvent,  TextField,  Typography,  useMediaQuery, useTheme } from '@mui/material';
import { useGetGrupos } from '../../hooks/useGrupos';
import { useContext, useEffect, useState } from 'react';
import { AlumnoType } from '../../types/types';
import { Cancel, CheckCircle, InsertComment } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useGetAlumnos } from '../../hooks/getAlumnos';
import { AddAsistenciaParams, useAddAsistencia, useEditAsistenciaManual } from '../../hooks/addAsistencia';
import { UserContext } from '../../contexts/userContext';
import FullScreenDialog from '../FullScreenDialog';
import { IndicatorButton } from '../IndicatorButton';

export default function Asistencia() {
  const { user } = useContext(UserContext);

  const alumnos = useGetAlumnos(user);
  const grupos = useGetGrupos(user);

  const [fechaDeAsistencia, setFechaDeAsistencia] = useState(dayjs());

  const [nuevaAsistencia, setNuevaAsistencia] = useState<AddAsistenciaParams | null>();
  const [nuevaAsistenciaM, setNuevaAsistenciaM] = useState<AddAsistenciaParams | null>();

  const [obsDialog, setObsDialog] = useState(false);
  const [filtro, setFiltro] = useState<GridFilterModel>(
    { items: [{ columnField: 'grupo', operatorValue: 'equals', value: localStorage.getItem('filtro-asistencia') ?? '' }] },
  );

  useEffect(() => {
    // save filter screen on cookie
    if (filtro) localStorage.setItem('filtro-asistencia', filtro.items[0].value);
  }, [filtro]);


  useAddAsistencia(nuevaAsistencia, user);
  const addAsistenciaApiCallManual = useEditAsistenciaManual(nuevaAsistenciaM, user);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (grupos.data?.length && !filtro.items[0].value) {
      setFiltro({ items: [{ columnField: 'grupo', operatorValue: 'equals', value: grupos.data[0].nombre }] });
    }
  }, [grupos.data]);

  useEffect(() => {
    alumnos.refetch();
  }, [addAsistenciaApiCallManual.data]);

  if (!user?._id) return null;

  const columns: GridColDef[] = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>Nombre</strong>),
      valueGetter: (params) => `${params.row.apellido} ${params.row.name}`,
      renderCell: (params) => {
        return (
          <Typography
            color="primary"
            style={{
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
              fontSize: 20,
            }}
          >
            {`${params.row.apellido} ${params.row.name}`}
          </Typography>
        );
      },
    },
    {
      field: 'grupo',
      headerName: 'Grupo',
      headerClassName: 'table-headers',
      hide: true,
    },
    {
      field: 'obs',
      flex: 1,
      headerName: 'Observaciones',
      headerClassName: 'table-headers',
      renderHeader: () => (<strong>Observaciones</strong>),
      hide: isMobile,
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        const obs = alumno.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day'))?.obs;
        return obs;
      },
    },
    {
      field: 'asistencia',
      headerName: 'Asistencia',
      headerClassName: 'table-headers',
      flex: 1,
      maxWidth: 120,
      minWidth: 120,
      renderHeader: () => '',
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        const estaPresente = alumno.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day') && a.asistencia === true);
        const estaAusente = alumno.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day') && a.asistencia === false);
        return (
          <div>
            <IconButton
              size='large'
              onClick={() => {
                setNuevaAsistencia({ id: alumno?._id, asistencia: 'presente', dia: fechaDeAsistencia.toISOString() });
                const diaExiste = alumnos.data?.find((a) => a.dni === alumno.dni)?.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day'));
                if (diaExiste) diaExiste.asistencia = true;
                else alumnos.data?.find((a) => a.dni === alumno.dni)?.asistencia.push({ asistencia: true, dia: fechaDeAsistencia.toISOString() });
              }}
              color={estaPresente ? 'success' : 'default'}
            >
              <CheckCircle />
            </IconButton>
            <IconButton
              size='large'
              onClick={() => {
                setNuevaAsistencia({ id: alumno?._id, asistencia: 'ausente', dia: fechaDeAsistencia.toISOString() });
                const diaExiste = alumnos.data?.find((a) => a.dni === alumno.dni)?.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day'));
                if (diaExiste) diaExiste.asistencia = false;
                else alumnos.data?.find((a) => a.dni === alumno.dni)?.asistencia.push({ asistencia: false, dia: fechaDeAsistencia.toISOString() });
              }}
              color={estaAusente ? 'error' : 'default'}
            >
              <Cancel />
            </IconButton>
          </div>
        );
      },
    },
    {
      field: 'observaciones',
      headerName: 'observaciones',
      headerClassName: 'table-headers',
      flex: 1,
      maxWidth: 70,
      minWidth: 70,
      renderHeader: () => '',
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        const asistenciaRow = alumnos.data?.find((a) => a.dni === alumno.dni)?.asistencia.find((a) => dayjs(a.dia).isSame(fechaDeAsistencia, 'day'));
        let asistencia:'presente' | 'ausente' | 'sin-clases' | null | undefined = undefined;
        if (!asistenciaRow) return null;

        if (asistenciaRow?.asistencia === true) asistencia = 'presente';
        if (asistenciaRow?.asistencia === false) asistencia = 'ausente';
        if (asistenciaRow?.asistencia === undefined) asistencia = 'sin-clases';
        if (asistenciaRow?.asistencia === null) asistencia = 'sin-clases';
        return (
          <IconButton
            size='large'
            onClick={() => {
              setNuevaAsistenciaM({ id: alumno?._id, dia: fechaDeAsistencia.toISOString(), obs: asistenciaRow.obs, asistencia });
              setObsDialog(true);
            }}
            color='default'
          >
            <InsertComment />
          </IconButton>
        );
      },
    },
  ];

  if (grupos.isLoading || !grupos.data || alumnos.isLoading || !alumnos.data || addAsistenciaApiCallManual.isLoading) return <LinearProgress />;

  return (
    <Box sx={{ height: isMobile ? 'calc(100vh - 115px)' : 'calc(100vh - 185px)', width: 1 }}>
      <FullScreenDialog
        fullscreen={isMobile}
        titulo='Agregar Observaciones'
        open={obsDialog}
        onClose={() => setObsDialog(false)}
      >
        <DialogContent>
          <TextField
            margin="dense"
            id="obs"
            label="Observaciones"
            type="text"
            fullWidth
            variant="standard"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNuevaAsistenciaM((prev) => ({ ...prev, obs: event.target.value }));
            }}
            value={nuevaAsistenciaM?.obs ?? ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={addAsistenciaApiCallManual.isLoading}
            onClick={() => {setObsDialog(false);}}
          >
          Descartar
          </Button>
          <IndicatorButton
            label='Guardar'
            loading={addAsistenciaApiCallManual.isLoading}
            onClick={() => {
              addAsistenciaApiCallManual.refetch();
              setObsDialog(false);
            }}
          />
        </DialogActions>
      </FullScreenDialog>
      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          <FormControl fullWidth>
            <Select
              input={<OutlinedInput sx={{ fontSize: 18, fontWeight: 'bold' }}/>}
              id="selector-grupo-asistencia"
              value={filtro.items[0].value as string ?? grupos.data[0]?.nombre ?? ''}
              onChange={(event: SelectChangeEvent) => setFiltro({ items: [{ columnField: 'grupo', operatorValue: 'equals', value: event.target.value }] })}
            >
              {grupos.data.map((grupo) => (
                <MenuItem key={grupo._id} value={grupo.nombre}>{grupo.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2} >
          <TextField
            style={{ marginLeft: 8, marginRight: 8 }}
            margin="dense"
            id="fecha-asistencia"
            label="Fecha"
            type="date"
            fullWidth
            variant="standard"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFechaDeAsistencia(dayjs(event.target.value, 'YYYY-MM-DD'));
            }}
            value={fechaDeAsistencia.format('YYYY-MM-DD')}
          />
        </Grid>
      </Grid>
      <DataGrid
        getRowHeight={() => 'auto'}
        hideFooter
        rows={alumnos.data}
        columns={columns}
        getRowId={(row) =>  row._id}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        filterModel={filtro}
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'nombre', sort: 'asc' }],
          },
        }}
      />
    </Box>
  );
}
